<template>
  <div class="page-container">
    <div class="page-header">
      <div class="header-left">
        <button class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="exportToExcel" :disabled="loading">
          <i class="ri-file-excel-line"></i>
          匯出 Excel
        </button>
        <button class="btn btn-secondary" @click="exportToCSV" :disabled="loading">
          <i class="ri-file-text-line"></i>
          匯出 CSV
        </button>
      </div>
    </div>

    <!-- 篩選表單 -->
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-item">
          <label>開始日期</label>
          <input
            v-model="filters.startDate"
            type="date"
            class="form-control"
            @change="handleFilterChange"
          />
        </div>
        <div class="filter-item">
          <label>結束日期</label>
          <input
            v-model="filters.endDate"
            type="date"
            class="form-control"
            @change="handleFilterChange"
          />
        </div>
        <div class="filter-item">
          <label>付款狀態</label>
          <select v-model="filters.status" @change="handleFilterChange" class="form-control">
            <option value="">全部</option>
            <option value="completed">已完成</option>
            <option value="failed">失敗</option>
            <option value="refunded">已退款</option>
          </select>
        </div>
        <div class="filter-item">
          <label>付款方式</label>
          <select v-model="filters.paymentType" @change="handleFilterChange" class="form-control">
            <option value="">全部</option>
            <option value="manual">人工收款</option>
            <option value="ecpay">綠界扣款</option>
          </select>
        </div>
        <div class="filter-item" v-if="isSystemAccount">
          <label>平台</label>
          <select v-model="filters.platformId" @change="handleFilterChange" class="form-control">
            <option value="">全部平台</option>
            <option v-for="platform in platforms" :key="platform.id" :value="platform.id">
              {{ platform.name }}
            </option>
          </select>
        </div>
        <div class="filter-item">
          <label>搜尋</label>
          <input
            v-model="filters.search"
            type="text"
            class="form-control"
            placeholder="客戶代碼或名稱..."
            @input="handleSearch"
          />
        </div>
      </div>
    </div>

    <!-- 統計資訊 -->
    <div class="summary-section" v-if="summary">
      <div class="summary-item">
        <span class="summary-label">總金額：</span>
        <span class="summary-value">NT$ {{ formatCurrency(summary.totalAmount) }}</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">已完成：</span>
        <span class="summary-value success">NT$ {{ formatCurrency(summary.completedAmount) }}</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">失敗：</span>
        <span class="summary-value danger">NT$ {{ formatCurrency(summary.failedAmount) }}</span>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchRecords">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th v-if="isSystemAccount">平台</th>
            <th>付款日期</th>
            <th>客戶代碼</th>
            <th>客戶名稱</th>
            <th>方案名稱</th>
            <th>付款金額</th>
            <th>付款狀態</th>
            <th>付款方式</th>
            <th>收款人</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="records.length === 0">
            <td :colspan="isSystemAccount ? 9 : 8" class="empty-state">
              沒有找到付款記錄
            </td>
          </tr>
          <tr v-for="record in records" :key="record.id">
            <td v-if="isSystemAccount">{{ record.platformName || '-' }}</td>
            <td>{{ formatDate(record.paymentDate) }}</td>
            <td>{{ record.clientCode }}</td>
            <td>{{ record.clientName }}</td>
            <td>{{ record.planName || '-' }}</td>
            <td>NT$ {{ formatCurrency(record.amount) }}</td>
            <td>
              <span :class="getStatusClass(record.status)">
                {{ getStatusText(record.status) }}
              </span>
            </td>
            <td>{{ record.paymentType === 'manual' ? '人工收款' : '綠界扣款' }}</td>
            <td>{{ record.collectorUsername || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分頁 -->
    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { billingService } from '@/api/modules/billing.js';
import { platformManagementService } from '@/api/modules/platforms.js';
import authService from '@/helpers/authservice/authService.js';
import { showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'PaymentRecords',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('付款記錄');

    const loading = ref(false);
    const error = ref(null);
    const records = ref([]);
    const summary = ref(null);
    const total = ref(0);
    const currentPage = ref(1);
    const pageSize = ref(20);
    const platforms = ref([]);
    const searchTimeout = ref(null);

    const filters = ref({
      startDate: '',
      endDate: '',
      status: '',
      paymentType: '',
      platformId: '',
      search: ''
    });

    const isSystemAccount = computed(() => {
      return authService.getAccountType() === 'system';
    });

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const fetchPlatforms = async () => {
      if (!isSystemAccount.value) return;
      try {
        const response = await platformManagementService.getPlatforms({ page: 1, pageSize: 100 });
        if (response.success) {
          platforms.value = (response.data.items || []).map(platform => ({
            id: platform.id,
            name: platform.platformName
          }));
        }
      } catch (err) {
        console.error('取得平台列表失敗:', err);
      }
    };

    const fetchRecords = async () => {
      loading.value = true;
      error.value = null;
      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value
        };

        // 只傳遞有值的篩選參數
        if (filters.value.startDate) {
          params.startDate = filters.value.startDate;
        }
        if (filters.value.endDate) {
          params.endDate = filters.value.endDate;
        }
        if (filters.value.status) {
          params.status = filters.value.status;
        }
        if (filters.value.paymentType) {
          params.paymentType = filters.value.paymentType;
        }
        if (filters.value.search) {
          params.search = filters.value.search;
        }
        // 系統帳號可以傳遞 platformId（如果有值）
        if (isSystemAccount.value && filters.value.platformId) {
          params.platformId = filters.value.platformId;
        }

        const response = await billingService.getPaymentRecords(params);
        if (response.success) {
          records.value = response.data.items || [];
          total.value = response.data.total || 0;
          summary.value = response.data.summary || null;
        } else {
          error.value = response.message || '取得付款記錄失敗';
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '取得付款記錄失敗';
        showError(error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchRecords();
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchRecords();
      }, 500);
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchRecords();
      }
    };

    const exportToExcel = async () => {
      try {
        loading.value = true;
        const params = { format: 'excel' };

        // 只傳遞有值的篩選參數
        if (filters.value.startDate) {
          params.startDate = filters.value.startDate;
        }
        if (filters.value.endDate) {
          params.endDate = filters.value.endDate;
        }
        if (filters.value.status) {
          params.status = filters.value.status;
        }
        if (filters.value.paymentType) {
          params.paymentType = filters.value.paymentType;
        }
        if (filters.value.search) {
          params.search = filters.value.search;
        }
        // 系統帳號可以傳遞 platformId（如果有值）
        if (isSystemAccount.value && filters.value.platformId) {
          params.platformId = filters.value.platformId;
        }

        const blob = await billingService.exportPaymentRecords(params);
        
        // 建立下載連結
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `payment-records-${filters.value.startDate || 'all'}-${filters.value.endDate || 'all'}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showSuccess('匯出成功', '付款記錄已匯出為 Excel 檔案');
      } catch (err) {
        showError('匯出失敗', err.response?.data?.message || err.message || '匯出失敗');
      } finally {
        loading.value = false;
      }
    };

    const exportToCSV = async () => {
      try {
        loading.value = true;
        const params = { format: 'csv' };

        // 只傳遞有值的篩選參數
        if (filters.value.startDate) {
          params.startDate = filters.value.startDate;
        }
        if (filters.value.endDate) {
          params.endDate = filters.value.endDate;
        }
        if (filters.value.status) {
          params.status = filters.value.status;
        }
        if (filters.value.paymentType) {
          params.paymentType = filters.value.paymentType;
        }
        if (filters.value.search) {
          params.search = filters.value.search;
        }
        // 系統帳號可以傳遞 platformId（如果有值）
        if (isSystemAccount.value && filters.value.platformId) {
          params.platformId = filters.value.platformId;
        }

        const blob = await billingService.exportPaymentRecords(params);
        
        // 建立下載連結
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `payment-records-${filters.value.startDate || 'all'}-${filters.value.endDate || 'all'}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showSuccess('匯出成功', '付款記錄已匯出為 CSV 檔案');
      } catch (err) {
        showError('匯出失敗', err.response?.data?.message || err.message || '匯出失敗');
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-TW');
    };

    const formatCurrency = (amount) => {
      if (!amount) return '0';
      return new Intl.NumberFormat('zh-TW').format(amount);
    };

    const getStatusClass = (status) => {
      if (status === 'completed') return 'badge badge-success';
      if (status === 'failed') return 'badge badge-danger';
      return 'badge badge-warning';
    };

    const getStatusText = (status) => {
      if (status === 'completed') return '已完成';
      if (status === 'failed') return '失敗';
      return '已退款';
    };

    const handleBack = () => {
      router.push('/system/billing');
    };

    onMounted(() => {
      fetchPlatforms();
      fetchRecords();
    });

    return {
      pageTitle,
      loading,
      error,
      records,
      summary,
      total,
      currentPage,
      totalPages,
      filters,
      platforms,
      isSystemAccount,
      fetchRecords,
      handleFilterChange,
      handleSearch,
      goToPage,
      exportToExcel,
      exportToCSV,
      formatDate,
      formatCurrency,
      getStatusClass,
      getStatusText,
      handleBack
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';

.page-container {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.filter-section {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
}

.filter-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.filter-item {
  display: flex;
  flex-direction: column;
}

.filter-item label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.summary-section {
  display: flex;
  gap: 24px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 24px;
}

.summary-item {
  display: flex;
  align-items: center;
}

.summary-label {
  font-size: 14px;
  color: #6b7280;
  margin-right: 8px;
}

.summary-value {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
}

.summary-value.success {
  color: #059669;
}

.summary-value.danger {
  color: #dc2626;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 48px;
}

.spinner {
  border: 3px solid #f3f4f6;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #dc2626;
  margin-bottom: 16px;
}
</style>
